import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Grid,
} from '@mui/material';
import {
  Security,
  VerifiedUser,
  Lock,
  Info,
  Policy,
} from '@mui/icons-material';

const PrivacyPolicy = () => {
  const theme = useTheme();

  const privacySections = [
    {
      icon: <Info sx={{ color: theme.palette.primary.main, fontSize: 40 }} />,
      title: 'Information We Collect',
      content: [
        'Personal identification information (Name, email address, phone number)',
        'Demographic information and professional details',
        'Usage data and website interaction metrics',
        'Device and browser information',
        'Location data with explicit consent'
      ]
    },
    {
      icon: <Security sx={{ color: theme.palette.primary.main, fontSize: 40 }} />,
      title: 'How We Use Your Information',
      content: [
        'Personalize and improve user experience',
        'Process transactions and service requests',
        'Send periodic emails and newsletters',
        'Respond to customer support inquiries',
        'Conduct research and analytics to enhance our services'
      ]
    },
    {
      icon: <Lock sx={{ color: theme.palette.primary.main, fontSize: 40 }} />,
      title: 'Information Protection',
      content: [
        'Implement advanced security measures to protect personal data',
        'Use SSL encryption for data transmission',
        'Restrict access to personal information to authorized personnel',
        'Regular security audits and vulnerability assessments',
        'Compliance with international data protection standards'
      ]
    },
    {
      icon: <VerifiedUser sx={{ color: theme.palette.primary.main, fontSize: 40 }} />,
      title: 'User Rights',
      content: [
        'Right to access your personal information',
        'Right to request data correction',
        'Right to request data deletion',
        'Right to opt-out of marketing communications',
        'Right to lodge a complaint with data protection authorities'
      ]
    }
  ];

  return (
    <Box sx={{ 
      bgcolor: '#f8f9fa', 
      minHeight: '100vh', 
      pt: 8, 
      pb: 12 
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 100,
                height: 4,
                bgcolor: theme.palette.primary.main
              }
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            At WTT International, we are committed to protecting your privacy and ensuring the confidentiality of your personal information.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {privacySections.map((section, index) => (
            <Grid item xs={12} md={6} key={section.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2 
                }}>
                  {section.icon}
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      ml: 2, 
                      fontWeight: 600, 
                      color: theme.palette.primary.main 
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>
                <List>
                  {section.content.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Policy sx={{ color: theme.palette.primary.light }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {item}
                          </Typography>
                        } 
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2, 
              fontWeight: 600, 
              color: theme.palette.primary.main 
            }}
          >
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            If you have any questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: theme.palette.primary.main, 
              fontWeight: 600 
            }}
          >
            privacy@wttint.com
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Last Updated: January 2024
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy; 